import React from "react";
import { CardList } from "@panda0/panda0-ui";
import { AlertCircle, Check, Flag, MonitorOff, Users, X } from "lucide-react";

import SnapshotsSection from "./snapshots-section";

const ProctoringSection = ({ proctoringRef }) => {
  const proctoringChecklist = [
    {
      icon: AlertCircle,
      title: "Camera Supervision",
      description: "Candidate looked away 3 times for 25 seconds",
      status: "warning",
      details: "Looked away at 10:23, 11:05, and 11:47 during the test",
    },
    {
      icon: Users,
      title: "Additional Persons",
      description: "No additional persons detected during the entire session",
      status: "success",
      details: "Environment scan completed successfully",
    },
    {
      icon: MonitorOff,
      title: "Screen Activity",
      description: "No screen sharing or recording detected",
      status: "success",
      details: "Browser was in full-screen mode throughout the test",
    },
  ];

  const statusColors = {
    success: {
      bg: "bg-green-50",
      border: "border-green-200",
      icon: "text-green-600",
      indicator: <Check className="h-5 w-5 text-white" />,
      indicatorBg: "bg-green-500",
    },
    warning: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      icon: "text-amber-600",
      indicator: <AlertCircle className="h-5 w-5 text-white" />,
      indicatorBg: "bg-amber-500",
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-200",
      icon: "text-red-600",
      indicator: <X className="h-5 w-5 text-white" />,
      indicatorBg: "bg-red-500",
    },
  };

  return (
    <div ref={proctoringRef} className="pb-10 pt-6 border-t" id="proctoring">
      <div className="flex items-center mb-6">
        <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3">
          <Flag className="h-4 w-4" />
        </div>
        <h2 className="text-xl font-bold">Proctoring Report</h2>
      </div>

      <div className="mb-4 px-1">
        <p className="text-gray-600">
          Automated monitoring was used to ensure test integrity. Here's a
          summary of the proctoring results:
        </p>
      </div>

      <div className="space-y-4">
        {proctoringChecklist.map((item, index) => {
          const ItemIcon = item.icon;
          const statusStyle = statusColors[item.status];

          return (
            <CardList.Card
              key={index}
              outerClassName={`border ${statusStyle.border} ${statusStyle.bg} shadow-sm overflow-hidden`}
            >
              <div className="flex p-4 items-start">
                <div
                  className={`rounded-full p-2 ${statusStyle.bg} ${statusStyle.icon} mr-4 flex-shrink-0`}
                >
                  <ItemIcon className="h-5 w-5" />
                </div>

                <div className="flex-grow">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">{item.title}</h3>
                    <div
                      className={`rounded-full ${statusStyle.indicatorBg} p-1 flex-shrink-0`}
                    >
                      {statusStyle.indicator}
                    </div>
                  </div>
                  <p className="text-gray-700 mt-1">{item.description}</p>
                  <p className="text-sm text-gray-500 mt-2">{item.details}</p>
                </div>
              </div>
            </CardList.Card>
          );
        })}
        <CardList.Card>
          <SnapshotsSection />
        </CardList.Card>
      </div>

      <div className="mt-6 bg-gray-50 border border-gray-100 rounded-lg p-4">
        <div className="flex items-center mb-2">
          <Check className="h-5 w-5 text-green-500 mr-2" />
          <h4 className="text-sm font-semibold">Overall Assessment</h4>
        </div>
        <p className="text-sm text-gray-600">
          Based on the proctoring results, this test session has been flagged
          for <span className="font-medium text-amber-600">manual review</span>.
          Minor irregularities were detected but they may not indicate a breach
          of test integrity.
        </p>
      </div>
    </div>
  );
};

export default React.memo(ProctoringSection);
